import {HttpParams, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {AnyAdapter} from '@drl/core/api/adapters/any-adapter.service';
import {ApiService} from '@drl/core/api/api.service';
import {AuthService} from '@drl/core/auth/auth.service';
import {User} from '@drl/core/auth/models/user.model';
import {ChatCategory} from '@drl/core/store/models/chat-category.model';
import {UserSessionStoreService} from '@drl/core/store/user-session-store.service';
import {NbMenuService} from '@nebular/theme';
import * as Pubnub from 'pubnub';
import {forkJoin, of, Subject, throwError} from 'rxjs';
import {concatMap, map, tap} from 'rxjs/operators';

import {AlertNotifAdapter} from './adapters';
import {Channels} from './channels-helper';
import {ChatNotifFacadeService} from './chat-notif-facade.service';
import {
  GetChatAccessCommand,
  GetNotificationUsersCommand,
  MarkNotifReadCommand,
} from './commands';
import {GetNotificationsCommand} from './commands/get-notifications.command';
import {AlertNotification} from './models/notification.model';
import {environment} from '@drl/env/environment';
import { RoleType } from '@drl/core/enums';
import { AlertStatus } from '@drl/shared/utility/alerts.enum';

const {subKey, pubKey} = environment;

@Injectable()
export class NotificationsService {
  constructor(
    private readonly apiService: ApiService,
    private readonly anyAdapter: AnyAdapter,
    private readonly store: UserSessionStoreService,
    private readonly menuService: NbMenuService,
    private readonly notifFacade: ChatNotifFacadeService,
    private readonly authService: AuthService,
    private readonly alertNotifAdapter: AlertNotifAdapter,
  ) {
    this.menuService.onItemClick().subscribe(event => {
      if (event.item.data && event.item.data.isLogout && this._pubnub) {
        this.unsubscribe();
      }
    });
  }

  private _pubnub: Pubnub = null;
  private _chatCategories: ChatCategory[] = [];
  private _currentUser: User = null;

  notificationMsg$: Subject<Pubnub.MessageEvent> = new Subject();
  alertNotificationMsg$: Subject<Pubnub.MessageEvent> = new Subject();

  getCurrentUserDetails() {
    return this.authService.currentUser().pipe(
      tap(user => {
        this._currentUser = user;
      }),
    );
  }

  getPubnub(cgIds?: number[]) {
    if (this._pubnub) {
      return of(this._pubnub);
    } else {
      return this.getCurrentUserDetails().pipe(
        concatMap(user => {
          return forkJoin([
            this.getChatAccess(),
            this.getChatCategories(),
          ]).pipe(
            map(resp => {
              this.subscribeToChannels(cgIds);
              return resp[0];
            }),
          );
        }),
      );
    }
  }

  getChatAccess() {
    const user = this.store.getUser();
    if (!user || !user.id || !this.store.getAccessToken()) {
      throwError(new Error('Unauthorized'));
      return null;
    }
    const command = new GetChatAccessCommand(
      this.apiService,
      this.anyAdapter,
      user.id,
    );

    command.parameters = {
      headers: new HttpHeaders().set(
        'pubnubToken',
        this.store.getPubnubToken(),
      ),
    };
    return command.execute().pipe(
      map(resp => {
        const {subKey, pubKey} = environment;
        this._pubnub = new Pubnub({
          subscribeKey: subKey,
          publishKey: pubKey,
          authKey: this.store.getPubnubToken(),
          uuid: 'alivio-chat',
        });
        return this._pubnub;
      }),
    );
  }

  getChatCategories() {
    if (this._chatCategories && this._chatCategories.length > 0) {
      return of(this._chatCategories);
    } else {
      return this.notifFacade.getCategories().pipe(
        tap(resp => {
          this._chatCategories = resp;
        }),
      );
    }
  }

  subscribeToChannels(cgIds: number[]) {
    const channelGps =
    this._chatCategories.map(cat =>
      Channels.groups.allPatientsCategoryIdChannelGroup(cat.id),
    );
    channelGps.push(Channels.groups.userIdChannelGroup(this._currentUser.id));
    this._pubnub.addListener({
      message: message => {
        if (message.message.msgData) {
          if (message.message.tenantName === this.store.getTenantKeyName()) {
            this.notificationMsg$.next(message);
          }
        } else {
          console.log(message);
          const options=message.message?.pn_apns
          if(options?.updatedBy!== this._currentUser.id&&options.type!=AlertStatus.Resolved) {
          this.alertNotificationMsg$.next(message);
          }
        }
      },
    });

    // subscribe channel based on caregroup
    let channel = [`user_${this._currentUser.id}`];

    if(cgIds.length > 0) {
      cgIds.forEach(id => {
        if(id != undefined && id != null) {
          if([RoleType.Admin.toString(),RoleType.Psychologist.toString()].includes(this._currentUser.role  as string)){
            channel.push(`IpsAlerts.caregroup_ap_${id}`);
          }
          if(![RoleType.Psychologist.toString()].includes(this._currentUser.role  as string)){
            channel.push(`IpsAlerts.caregroup_${id}`);
          }

        }
      })
    }
    this._pubnub.subscribe({
      channels: channel,
      channelGroups: channelGps,
    });
  }

  unsubscribe() {
    this.notificationMsg$.complete();
    this.alertNotificationMsg$.complete();
    this._pubnub.unsubscribeAll();
  }

  // sonarignore:start
  getAllNotifications(offset = 0, limit = 0) {
    // sonarignore:end
    const command: GetNotificationsCommand<AlertNotification> =
      new GetNotificationsCommand(this.apiService, this.alertNotifAdapter);
      let  queryStr:string;
      if(limit>=1){
       queryStr = JSON.stringify({
        offset,
        limit,
      });
    }else{
       queryStr = JSON.stringify({
        offset,
      });
    }

    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };

    return command.execute();
  }

  getNotificationUsers(notifIds: string[]) {
    // sonarignore:start
    const command: GetNotificationUsersCommand<any> =
      new GetNotificationUsersCommand(this.apiService, this.anyAdapter);
    // sonarignore:end

    const queryStr = JSON.stringify({
      where: {
        notificationId: {
          inq: notifIds,
        },
      },
    });

    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };

    return command.execute();
  }

  markNotifsAsRead(
    data: {notificationId: string; userId: number; isRead: boolean}[],
  ) {
    // sonarignore:start
    const command: MarkNotifReadCommand<any> = new MarkNotifReadCommand(
      this.apiService,
      this.anyAdapter,
    );
    // sonarignore:end

    command.parameters = {
      data,
    };

    return command.execute();
  }
}
